import './profile.scss';
import {
  useCallback, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Else, If, Then } from 'react-if';
import PageHeader from '../../shared/blocks/page-header/page-header';
import Loader from '../../shared/blocks/loader/loader';
import useUpdateAccountSettings from '../../../utilities/hooks/useUpdateAccountSettings';
import ProfileForm from '../../shared/forms/profile-form';
import BannerMessage from '../../shared/blocks/banner-message';
import { getPhoneDigitsOnly } from '../../../utilities/utils';
import EditEmailModal from '../../shared/modals/edit-email-modal';
import useUpdateEmail from '../../../utilities/hooks/useUpdateEmail';

const Profile = () => {
  const location = useLocation();
  const profile = useSelector((state) => state.accounts.user.profile);
  const defaultValues = useMemo(() => ({
    firstName: profile.given_name,
    lastName: profile.family_name,
    phoneNumber: getPhoneDigitsOnly(profile.phone_number),
    email: profile.email,
  }), [profile]);
  const { update: updateEmail, isLoading: emailUpdating } = useUpdateEmail();
  const [emailToVerify, setEmailToVerify] = useState('');
  const { update: updateAccountSettings, isLoading, isSuccess } = useUpdateAccountSettings();
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isEmailVerifyModalOpened, setIsEmailVerifyModalOpened] = useState(false);

  const handleSubmit = useCallback((formValues) => {
    const phoneNumber = getPhoneDigitsOnly(formValues.phoneNumber);
    if (formValues.firstName !== defaultValues.firstName
      || formValues.lastName !== defaultValues.lastName
      || phoneNumber !== defaultValues.phoneNumber
    ) {
      updateAccountSettings({
        ...formValues,
        phoneNumber: `+1${phoneNumber}`,
      });
    }
    if (formValues.email !== defaultValues.email) {
      updateEmail(formValues, {
        onSuccess: () => {
          setEmailToVerify(formValues.email);
          setIsEmailVerifyModalOpened(true);
        },
      });
    }
  }, [defaultValues, updateAccountSettings, updateEmail]);

  const handleEmailVerifyModalClose = useCallback((success) => {
    if (success) {
      setIsEmailVerified(true);
    }
    setIsEmailVerifyModalOpened(false);
  }, []);

  return (
    <div className="profile-page">
      {isEmailVerified && <BannerMessage type="success" description={`Your email has been successfully changed to ${emailToVerify}`} />}
      {isSuccess && <BannerMessage type="success" description="Your profile have been updated." />}

      <PageHeader title="Personal info" subtitle="* indicates required field" />

      {location.state?.passwordResetSuccess && (
        <BannerMessage type="success" description="Your password has been updated." />
      )}

      <If condition={Object.keys(profile).length}>
        <Then>
          <ProfileForm
            onSubmit={handleSubmit}
            defaultValues={defaultValues}
            submitting={isLoading || emailUpdating}
          />
        </Then>
        <Else>
          <Loader />
        </Else>
      </If>

      {emailToVerify && isEmailVerifyModalOpened ? (
        <EditEmailModal
          emailToVerify={emailToVerify}
          onClose={handleEmailVerifyModalClose}
        />
      ) : null}

    </div>
  );
};

export default Profile;
